
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Building2, User, Users, Calendar, PoundSterling, Camera, Image, ArrowRight, FileText, Trophy, XCircle, Archive, RotateCcw } from "lucide-react";
import { useUpdateDeal, useArchiveDeal, useRestoreDeal } from "@/hooks/use-deals";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";

interface DealCardProps {
  deal: any;
  stages: any[];
  onClick?: () => void;
  isArchived?: boolean;
}

export const DealCard = ({
  deal,
  stages,
  onClick,
  isArchived = false
}: DealCardProps) => {
  const updateDeal = useUpdateDeal();
  const archiveDeal = useArchiveDeal();
  const restoreDeal = useRestoreDeal();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [sendingProposal, setSendingProposal] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const isContactAdmin = () => {
    if (!deal.contact) return false;
    // Check if contact has admin or superuser role via intended_system_role or actual user_role
    return deal.contact.intended_system_role === 'admin' || 
           deal.contact.intended_system_role === 'superuser' ||
           deal.contact.user_role === 'admin' ||
           deal.contact.user_role === 'superuser';
  };

  const isContactActive = () => {
    if (!deal.contact) return false;
    // Contact is active if they have a user_id (linked to auth) and user_status is active
    return deal.contact.user_id && deal.contact.user_status === 'active';
  };

  const canSendProposal = () => {
    return isContactAdmin() && isContactActive() && deal.contact?.email;
  };

  const getProposalValidationMessage = () => {
    if (!deal.contact?.email) return "No contact email found";
    if (!isContactAdmin()) return "Contact must be an Admin or Superuser to receive proposals";
    if (!isContactActive()) return "Contact must have an Active account to receive proposals";
    return null;
  };

  const getAssigneeInfo = () => {
    console.log("Getting assignee info for deal:", deal.id, "Deal data:", deal);

    // First check for direct staff assignment
    if (deal.assigned_staff) {
      console.log("Found assigned staff:", deal.assigned_staff);
      return {
        name: `${deal.assigned_staff.first_name} ${deal.assigned_staff.last_name}`,
        email: deal.assigned_staff.email,
        type: 'Staff',
        icon: User,
        source: 'assigned'
      };
    }
    // Then check for direct partner assignment
    else if (deal.assigned_partner) {
      console.log("Found assigned partner:", deal.assigned_partner);
      return {
        name: `${deal.assigned_partner.first_name} ${deal.assigned_partner.last_name}`,
        email: deal.assigned_partner.email,
        type: 'Partner',
        icon: Users,
        source: 'assigned'
      };
    }
    // Fallback to contact owner if no direct assignment
    else if (deal.contact) {
      console.log("Checking contact owner. Contact data:", deal.contact);

      // Check for staff owner through contact
      if (deal.contact.internal_owner_id && deal.contact.staff_owner) {
        console.log("Found staff owner via contact:", deal.contact.staff_owner);
        return {
          name: `${deal.contact.staff_owner.first_name} ${deal.contact.staff_owner.last_name}`,
          email: deal.contact.staff_owner.email,
          type: 'Staff',
          icon: User,
          source: 'contact_owner'
        };
      }
      // Check for partner owner through contact
      else if (deal.contact.external_owner_id && deal.contact.partner_owner) {
        console.log("Found partner owner via contact:", deal.contact.partner_owner);
        return {
          name: `${deal.contact.partner_owner.first_name} ${deal.contact.partner_owner.last_name}`,
          email: deal.contact.partner_owner.email,
          type: 'Partner',
          icon: Users,
          source: 'contact_owner'
        };
      }
    }
    console.log("No assignee found for deal:", deal.id);
    return null;
  };

  const handleCardClick = () => {
    // Call onClick for all card clicks to open deal calculator
    if (onClick) {
      onClick();
    }
  };

  const handleStageProgression = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentStage = stages.find(stage => stage.id === deal.stage_id);
    
    // If current stage is Opportunity, move to Proposal
    if (currentStage?.name === 'Opportunity') {
      const proposalStage = stages.find(stage => stage.name === 'Proposal');
      if (proposalStage) {
        try {
          await updateDeal.mutateAsync({
            id: deal.id,
            data: {
              stage_id: proposalStage.id
            }
          });
          toast({
            title: "Stage Updated",
            description: `Deal moved to ${proposalStage.name} stage`
          });
        } catch (error) {
          console.error("Error updating deal stage:", error);
        }
      }
    }
    // If current stage is Proposal, send proposal
    else if (currentStage?.name === 'Proposal') {
      await handleSendProposal();
    }
  };

  const handleSendProposal = async () => {
    const validationMessage = getProposalValidationMessage();
    if (validationMessage) {
      toast({
        title: "Cannot Send Proposal",
        description: validationMessage,
        variant: "destructive"
      });
      return;
    }

    setSendingProposal(true);
    try {
      const assignee = getAssigneeInfo();
      console.log('Sending proposal notification with data:', {
        dealId: deal.id,
        contactEmail: deal.contact.email,
        contactName: `${deal.contact.first_name} ${deal.contact.last_name}`,
        dealTitle: deal.title,
        totalCameras: deal.total_cameras || 0,
        totalImages: deal.total_images || 0,
        dealValue: deal.value || 0,
        organizationName: deal.organization?.name || '',
        assigneeName: assignee?.name || '',
        assigneeType: assignee?.type || '',
        assigneeEmail: assignee?.email || ''
      });

      const { data, error } = await supabase.functions.invoke('send-proposal-notification', {
        body: {
          dealId: deal.id,
          contactEmail: deal.contact.email,
          contactName: `${deal.contact.first_name} ${deal.contact.last_name}`,
          dealTitle: deal.title,
          totalCameras: deal.total_cameras || 0,
          totalImages: deal.total_images || 0,
          dealValue: deal.value || 0,
          organizationName: deal.organization?.name || '',
          organizationId: deal.organization_id || deal.contact?.organization_id, // Add organization ID
          assigneeName: assignee?.name || '',
          assigneeType: assignee?.type || '',
          assigneeEmail: assignee?.email || '',
          baseUrl: window.location.origin
        }
      });

      if (error) {
        console.error('Function invocation error:', error);
        throw error;
      }

      console.log('Proposal notification response:', data);

      // Proposal sent successfully - deal stays in Proposal stage until customer accepts
      toast({
        title: "Proposal Sent",
        description: data.message || `Proposal sent successfully to ${deal.contact.email}. Deal will move to Won status when customer accepts.`
      });
      
      // Refresh deals data to show updated state
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    } catch (error) {
      console.error("Error sending proposal:", error);
      toast({
        title: "Error",
        description: "Failed to send proposal. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingProposal(false);
    }
  };

  const handleArchive = () => {
    archiveDeal.mutate({
      dealId: deal.id,
      reason: archiveReason.trim() || undefined
    });
    setArchiveDialogOpen(false);
    setArchiveReason("");
  };

  const handleRestore = () => {
    restoreDeal.mutate(deal.id);
  };

  const canProgress = () => {
    // Don't allow progression if deal is closed or archived
    if (deal.archived || deal.status === 'won' || deal.status === 'lost') {
      return false;
    }
    
    const currentStage = stages.find(stage => stage.id === deal.stage_id);
    
    // Allow progression from Opportunity to Proposal
    if (currentStage?.name === 'Opportunity') {
      return true;
    }
    
    // Allow sending proposal from Proposal stage only if contact can receive proposals
    if (currentStage?.name === 'Proposal') {
      return canSendProposal();
    }
    
    return false;
  };

  const canBeArchived = () => {
    // Can archive if it's an opportunity or proposal and not already closed/archived
    const currentStage = stages.find(stage => stage.id === deal.stage_id);
    const isOpportunityOrProposal = currentStage?.name === 'Opportunity' || currentStage?.name === 'Proposal';
    const isNotClosed = deal.status !== 'won' && deal.status !== 'lost' && !deal.archived;
    return isOpportunityOrProposal && isNotClosed;
  };

  const getProgressionButtonText = () => {
    const currentStage = stages.find(stage => stage.id === deal.stage_id);
    
    if (currentStage?.name === 'Opportunity') {
      return 'Move to Proposal';
    }
    
    if (currentStage?.name === 'Proposal') {
      // Check if proposal has been sent before
      return deal.proposal_sent_at ? 'Resend Proposal' : 'Send Proposal';
    }
    
    return 'Next Stage';
  };

  const getProgressionButtonIcon = () => {
    const currentStage = stages.find(stage => stage.id === deal.stage_id);
    
    if (currentStage?.name === 'Proposal') {
      return <FileText className="h-3 w-3 mr-1" />;
    }
    
    return <ArrowRight className="h-3 w-3 mr-1" />;
  };

  const getClosedStatusBadge = () => {
    if (deal.archived) {
      return <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">
          <Archive className="h-3 w-3 mr-1" />
          Archived
        </Badge>;
    }
    switch (deal.status) {
      case 'won':
        return <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
            <Trophy className="h-3 w-3 mr-1" />
            Won
          </Badge>;
      case 'lost':
        return <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
            <XCircle className="h-3 w-3 mr-1" />
            Lost
          </Badge>;
      default:
        return null;
    }
  };

  const assignee = getAssigneeInfo();
  const currentStage = stages.find(stage => stage.id === deal.stage_id);
  const isOpportunity = currentStage?.name === 'Opportunity';
  const isDealClosed = deal.status === 'won' || deal.status === 'lost' || deal.archived;
  const isCustomerInitiated = deal.deal_data?.customer_initiated === true;
  const selfServiceType = deal.deal_data?.self_service_type;

  return (
    <>
      <Card className={`cursor-pointer hover:shadow-lg transition-all duration-300 mb-3 bg-slate-50 border-slate-200 hover:border-teal-300 rounded-lg shadow-sm ${isOpportunity && !isArchived ? 'hover:bg-teal-50' : ''} ${isDealClosed ? 'opacity-75' : ''}`} onClick={handleCardClick}>
        <CardHeader className="pb-3 bg-lime-button text-black rounded-t-lg">
          <div className="flex items-start justify-between">
            <div className="flex-1 text-slate-900">
              <h4 className="font-semibold text-sm line-clamp-2 text-slate-900 mb-2 my-[8px]">{deal.title}</h4>
              <div className="flex flex-wrap gap-2 mb-2">
                {isCustomerInitiated && <Badge className="bg-blue-500 text-white font-semibold text-xs px-3 py-1 whitespace-nowrap">
                    Customer Initiated
                  </Badge>}
                {selfServiceType === 'order' && <Badge className="bg-green-500 text-white font-semibold text-xs">
                    Direct Order
                  </Badge>}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-2">
              {deal.value > 0}
              {isArchived && <Button size="sm" variant="outline" className="h-6 px-2 text-xs bg-blue-100 border-blue-300 text-blue-700 hover:bg-blue-200" onClick={e => {
              e.stopPropagation();
              handleRestore();
            }}>
                  <RotateCcw className="h-3 w-3" />
                </Button>}
              {canBeArchived() && <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-xs bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200" onClick={e => e.stopPropagation()}>
                      <Archive className="h-3 w-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white" onClick={e => e.stopPropagation()}>
                    <DialogHeader>
                      <DialogTitle>Archive {currentStage?.name || 'Deal'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        Are you sure you want to archive this {currentStage?.name?.toLowerCase() || 'deal'}? 
                        It will be moved to the archive section.
                      </p>
                      <div>
                        <Label htmlFor="archive-reason">Reason (Optional)</Label>
                        <Textarea id="archive-reason" value={archiveReason} onChange={e => setArchiveReason(e.target.value)} placeholder="Why are you archiving this deal?" className="mt-1" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleArchive} disabled={archiveDeal.isPending} className="bg-lime-button hover:bg-lime-button/90 text-black">
                          {archiveDeal.isPending ? "Archiving..." : "Archive"}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>}
            </div>
          </div>
          {(isOpportunity || currentStage?.name === 'Proposal') && !isDealClosed && !isArchived && <div className="text-xs text-slate-900 flex items-center gap-1 mt-2">
              <FileText className="h-3 w-3" />
              Click to preview proposal
            </div>}
          {isArchived && <div className="text-xs text-gray-200 flex items-center gap-1 mt-2">
              <Archive className="h-3 w-3" />
              Archived {deal.archived_at && new Date(deal.archived_at).toLocaleDateString()}
            </div>}
        </CardHeader>

        <CardContent className="pt-4 space-y-3 bg-slate-50">
          {/* Deal Status - Show closed status badge only for closed deals */}
          {isDealClosed && <div className="flex items-center gap-2">
              {getClosedStatusBadge()}
              {deal.deal_data?.lost_reason && <span className="text-xs text-slate-600">
                  Reason: {deal.deal_data.lost_reason}
                </span>}
              {deal.archived_reason && <span className="text-xs text-slate-600">
                  Reason: {deal.archived_reason}
                </span>}
            </div>}

          {deal.contact && <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <User className="h-3 w-3 text-teal-600" />
                <span>{deal.contact.first_name} {deal.contact.last_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`text-xs px-2 py-0.5 ${isContactAdmin() ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                  {isContactAdmin() ? '✅ Admin/Superuser' : '❌ Not Admin/Superuser'}
                </Badge>
                <Badge className={`text-xs px-2 py-0.5 ${isContactActive() ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-800 border-red-200'}`}>
                  {isContactActive() ? '✅ Active Account' : '❌ No Account'}
                </Badge>
              </div>
            </div>}
          
          {deal.organization && <div className="flex items-center gap-2 text-sm text-slate-700">
              <Building2 className="h-3 w-3 text-teal-600" />
              <span>{deal.organization.name}</span>
            </div>}
          
          {assignee && <div className="flex items-center gap-2 text-sm">
              <Avatar className="h-5 w-5 border border-teal-200">
                <AvatarFallback className="text-xs bg-teal-100 text-teal-700">
                  {assignee.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-slate-600">
                {assignee.name} ({assignee.type})
                {assignee.source === 'contact_owner' && <span className="text-teal-600 ml-1">(via Contact Owner)</span>}
              </span>
            </div>}
          
          <div className="flex items-center text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3 text-teal-600" />
              <span>{new Date(deal.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          
          {(deal.total_cameras > 0 || deal.total_images > 0) && <div className="flex gap-4 text-xs text-slate-600 pt-2 border-t border-slate-200">
              {deal.total_cameras > 0 && <div className="flex items-center gap-1">
                  <Camera className="h-3 w-3 text-yellow-600" />
                  <span>{deal.total_cameras} cameras</span>
                </div>}
              {deal.total_images > 0 && <div className="flex items-center gap-1">
                  <Image className="h-3 w-3 text-yellow-600" />
                  <span>{deal.total_images} images</span>
                </div>}
            </div>}

          {(deal.total_revenue > 0 || deal.image_revenue > 0) && <div className="flex gap-4 text-xs text-slate-700 pt-2">
              {deal.total_revenue > 0 && <div className="flex items-center gap-1">
                  <PoundSterling className="h-3 w-3 text-teal-600" />
                  <span>Total: {formatCurrency(deal.total_revenue)}</span>
                </div>}
              {deal.image_revenue > 0 && <div className="flex items-center gap-1">
                  <PoundSterling className="h-3 w-3 text-yellow-600" />
                  <span>Images: {formatCurrency(deal.image_revenue)}</span>
                </div>}
            </div>}

          {!isDealClosed && !isArchived && (
            <div className="pt-3 border-t border-slate-200 space-y-2">
              {canProgress() && (
                <Button 
                  onClick={handleStageProgression} 
                  disabled={updateDeal.isPending || sendingProposal || (currentStage?.name === 'Proposal' && !canSendProposal())} 
                  size="sm" 
                  className="w-full bg-lime-button hover:bg-lime-button/90 text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateDeal.isPending || sendingProposal ? "Processing..." : (
                    <>
                      {getProgressionButtonIcon()}
                      {getProgressionButtonText()}
                    </>
                  )}
                </Button>
              )}
              
              {/* Show validation message when can't send proposal */}
              {currentStage?.name === 'Proposal' && !canSendProposal() && !isDealClosed && !isArchived && (
                <div className="text-xs text-red-600 text-center bg-red-50 p-2 rounded border border-red-200">
                  {getProposalValidationMessage()}
                </div>
              )}
              
              {/* Show note about customer acceptance for Proposal stage */}
              {currentStage?.name === 'Proposal' && canSendProposal() && (
                <p className="text-xs text-slate-600 text-center italic">
                  Deal will move to Won when customer accepts proposal
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

    </>
  );
};
